import React, { useEffect } from "react";
import { Lock, ExternalLink, X, Sparkles, Check } from "lucide-react";
import { usePrice } from "@/lib/price";

const WHOP_FALLBACK = "https://whop.com/checkout/plan_hfsvR7lsOjQds";

/**
 * UnlockModal — mini upsell shown when a free user taps a locked tool.
 * Single hard CTA: Whop checkout in a new tab. Price + scarcity copy
 * are pulled from /api/price so they always reflect the live launch tier.
 */
export default function UnlockModal({ open, tool, onClose }) {
  const { price, remaining, cap, checkout } = usePrice();
  const whop = checkout || WHOP_FALLBACK;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="unlock-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="unlock-modal-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-5 bg-[#0a0a0a]/70"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-[#FFC400] text-[#0a0a0a] border-4 border-[#0a0a0a] shadow-brutal-lg"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="unlock-modal-close"
          className="absolute -top-3 -right-3 w-9 h-9 bg-[#0a0a0a] text-[#FFC400] border-4 border-[#FFC400] flex items-center justify-center shadow-brutal-sm"
        >
          <X size={16} strokeWidth={3} />
        </button>
        <div className="p-6 md:p-8">
          <div className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFC400] border-2 border-[#0a0a0a] px-2.5 py-1 font-accent text-xs">
            <Lock size={12} strokeWidth={3} /> ALL-ACCESS ONLY
          </div>
          <h2
            id="unlock-modal-title"
            className="font-display text-3xl md:text-4xl leading-[0.95] mt-4"
          >
            Unlock{" "}
            <span className="bg-[#0a0a0a] text-[#FFC400] px-2">{tool || "everything"}</span>{" "}
            for <span className="bg-[#E10600] text-white px-2">{price}</span>.
          </h2>
          <p className="font-mono-body text-sm md:text-base mt-4 leading-relaxed text-[#0a0a0a]/80">
            One-time payment. Lifetime access. Every JobHacki tool + the community + everything we ship next.
          </p>
          {remaining && cap && (
            <div
              data-testid="unlock-modal-scarcity"
              className="font-mono-body text-xs text-[#0a0a0a]/75 bg-[#0a0a0a]/10 border-2 border-[#0a0a0a] mt-4 px-3 py-2"
            >
              <b className="text-[#E10600]">{remaining.toLocaleString()}</b> of {cap.toLocaleString()} launch spots left at <b>{price}</b>.
            </div>
          )}
          <ul className="space-y-2 mt-5 font-mono-body text-sm">
            {[
              "Resume Builder · Simulator · OmniCut · OmniScript",
              "Every live ATS job · Playbook & Tool directories",
              "7-day refund via Whop · no subscription",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <Check size={16} strokeWidth={3} className="mt-0.5 shrink-0 text-[#E10600]" />
                {line}
              </li>
            ))}
          </ul>
          <a
            href={whop}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="unlock-modal-cta"
            className="hover-pop mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#0a0a0a] text-[#FFC400] border-4 border-[#0a0a0a] px-5 py-4 font-accent text-base shadow-brutal"
          >
            <Sparkles size={16} strokeWidth={3} /> Unlock for {price}
            <ExternalLink size={14} strokeWidth={3} />
          </a>
          <button
            onClick={onClose}
            className="mt-3 w-full text-center font-mono-body text-xs text-[#0a0a0a]/55 hover:text-[#0a0a0a]"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
